import React, { useMemo, useRef, useState } from "react";
import { Image as PdfImage, StyleSheet, Text, View } from "@react-pdf/renderer";
import DataList from "components/datatable/DataList";
import useAxios from "hooks/useAxios";
import useAxiosFunction from "hooks/useAxiosFunction";
import useMsgBox from "hooks/useMsgBox";
import usePDFRenderer from "hooks/usePDFRenderer";
import { useAuth } from "context/AuthProvider";
import { MyConst } from "data/MyConst";
import { RuleRequired, postgresDateToIndonesia } from "utils/MyUtils";
import {
  PersonalAchievementTable,
  PersonalCertificationTable,
} from "features/kms/profile/PersonalCapaianPages";
import { formatPersonName } from "features/kms/common/nameFormat";
import { boolText, hasPermission, showEmpty } from "features/kms/system/SystemPageUtils";

function addSimpleReferenceConfig(title, url, idColumn, label) {
  return {
    title,
    url,
    cellid: idColumn,
    columns: ["nama"],
    coltype: {
      nama: {
        type: "text",
        label,
        name: "nama",
        validation: RuleRequired,
        colsize: 12,
      },
    },
    optionsUrl: url,
    optionsAuth: true,
    otherLabel: "Lainnya",
  };
}

function formatDate(value) {
  return value ? postgresDateToIndonesia(value) || value : "";
}

function renderPeriod(value, col, item) {
  const start = formatDate(item?.tanggal_mulai);
  const end = item?.tanggal_selesai ? formatDate(item.tanggal_selesai) : "Sekarang";
  return `${showEmpty(start)} s.d. ${end || "Sekarang"}`;
}

function formatWholeNumber(value) {
  if (value === null || value === undefined || value === "") return "";
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return String(value);
  return new Intl.NumberFormat("id-ID", {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(numeric);
}

function detailValue(value) {
  return showEmpty(value);
}

function formatFullName(item) {
  return formatPersonName(item, ["nama_lengkap"]);
}

function formatAge(value) {
  if (value === null || value === undefined || value === "") return "";
  return `${formatWholeNumber(value)} Tahun`;
}

function formatBankAccount(item) {
  const bank = String(item?.bank || "").trim();
  const accountNumber = String(item?.nomor_rekening || "").trim();
  const accountName = String(item?.rekening_atas_nama || "").trim();
  const firstPart = [bank, accountNumber].filter(Boolean).join(" ");
  const parts = [];
  if (firstPart) parts.push(firstPart);
  if (accountName) parts.push(`a.n. ${accountName}`);
  return parts.join(" ");
}

function pengurusHistoryItems(item) {
  return Array.isArray(item?.pengurus_history) ? item.pengurus_history : [];
}

function membershipHistoryItems(item) {
  return Array.isArray(item?.membership_history) ? item.membership_history : [];
}

function educationHistoryItems(item) {
  return Array.isArray(item?.education_history) ? item.education_history : [];
}

function achievementHistoryItems(item) {
  return Array.isArray(item?.achievement_history) ? item.achievement_history : [];
}

function certificationHistoryItems(item) {
  return Array.isArray(item?.certification_history) ? item.certification_history : [];
}

function photoAPIAssetSource(filename) {
  return filename
    ? `${String(MyConst.URLAPI || "").replace(/\/$/, "")}/file/asset/personal/${encodeURIComponent(filename)}`
    : "";
}

function personalAssetSource(filename) {
  return filename
    ? `${String(MyConst.URLASET || "").replace(/\/$/, "")}/personal-document/${encodeURIComponent(filename)}`
    : "";
}

async function loadPhotoDataURL(filename, token) {
  const url = photoAPIAssetSource(filename);
  if (!url) return "";

  try {
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) return "";

    const blob = await response.blob();
    return await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || "");
      reader.onerror = () => resolve("");
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    return "";
  }
}

function DetailField({ label, value, size = 4 }) {
  return (
    <div className={`col-md-${size} mb-3`}>
      <div className="text-xs text-uppercase text-muted font-weight-bold">{label}</div>
      <div className="border-bottom pb-2">{detailValue(value)}</div>
    </div>
  );
}

function DetailSection({ children }) {
  return (
    <div className="card mb-3">
      <div className="card-body pb-1">
        <div className="row">{children}</div>
      </div>
    </div>
  );
}

function supportingDocumentItems(item) {
  return [
    { label: "Kartu Keluarga", file: item?.file_kartu_keluarga },
    { label: "KTP", file: item?.file_ktp },
    { label: "Akta Lahir", file: item?.file_akta_lahir },
    { label: "Rekening Bank", file: item?.file_rekening_bank },
  ].filter((doc) => String(doc.file || "").trim() !== "");
}

function SupportingDocumentsSection({ item }) {
  const documents = supportingDocumentItems(item);
  if (documents.length < 1) return null;

  return (
    <div className="card mb-3">
      <div className="card-header bg-light">
        <h3 className="card-title font-nol-koma-sembilan">Dokumen Pendukung</h3>
      </div>
      <div className="card-body">
        <div className="row">
          {documents.map((doc) => (
            <div className="col-md-3 mb-3" key={doc.label}>
              <div className="border rounded p-3 h-100 bg-light">
                <div className="text-xs text-uppercase text-muted font-weight-bold mb-2">
                  {doc.label}
                </div>
                <a
                  href={personalAssetSource(doc.file)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-outline-primary btn-sm"
                >
                  <i className="fa fa-file-alt mr-1"></i>
                  Lihat Dokumen
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PengurusProfileDetail({ item }) {
  const [activeTab, setActiveTab] = useState("identitas");
  const photoUrl = item?.photo_url
    ? `${String(MyConst.URLASET || "").replace(/\/$/, "")}/personal/${item.photo_url}`
    : `${process.env.PUBLIC_URL || ""}/assets/dist/img/avatar.png`;
  const fullName = formatFullName(item);
  const birthPlaceDate = [item?.tempat_lahir, formatDate(item?.tanggal_lahir)].filter(Boolean).join(", ");
  const bankAccount = formatBankAccount(item);
  const history = pengurusHistoryItems(item);
  const membershipHistory = membershipHistoryItems(item);
  const educationHistory = educationHistoryItems(item);
  const achievementHistory = achievementHistoryItems(item);
  const certificationHistory = certificationHistoryItems(item);
  const capaianBaseUrl = item?.id_pengurus_organisasi
    ? `/organisasi-pengurus/${item.id_pengurus_organisasi}`
    : "";

  const tabs = [
    { id: "identitas", label: "Identitas" },
    { id: "pekerjaan", label: "Pekerjaan" },
    { id: "orang_tua", label: "Orang Tua" },
    { id: "asuransi", label: "Asuransi" },
    { id: "pendidikan", label: "Pendidikan" },
    { id: "prestasi", label: "Prestasi" },
    { id: "sertifikasi", label: "Sertifikasi" },
    { id: "keanggotaan", label: "Keanggotaan" },
    { id: "riwayat", label: "Riwayat Organisasi" },
  ];

  return (
    <div className="sdm-profile-detail">
      <div className="card bg-light mb-3">
        <div className="card-body">
          <div className="d-flex flex-column flex-md-row align-items-center">
            <div className="text-center">
              <img
                src={photoUrl}
                alt={fullName || "Profil Pengurus"}
                className="img-circle elevation-2 bg-white"
                style={{
                  width: 112,
                  height: 112,
                  objectFit: "cover",
                  objectPosition: "center",
                  border: "3px solid #fff",
                }}
              />
            </div>
            <div className="ml-md-4 mt-3 mt-md-0 text-center text-md-left flex-fill">
              <div className="text-uppercase text-sm font-weight-bold text-muted">PROFIL PENGURUS</div>
              <h3 className="mb-1">{detailValue(fullName)}</h3>
              <div className="text-muted mb-2">
                <span className="mr-4">
                  <strong>Jabatan</strong>: {detailValue(item?.jabatan)}
                </span>
              </div>
              <div>
                <span className={`badge ${item?.aktif ? "badge-success" : "badge-secondary"} mr-2`}>
                  {boolText(item?.aktif)}
                </span>
                <span className="text-muted">
                  <strong>Sejak</strong>: {detailValue(formatDate(item?.tanggal_mulai))}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card card-primary card-outline card-outline-tabs">
        <div className="card-header p-0 border-bottom-0">
          <ul className="nav nav-tabs" role="tablist">
            {tabs.map((tab) => (
              <li className="nav-item" key={tab.id}>
                <button
                  type="button"
                  className={`nav-link px-4 py-2 ${activeTab === tab.id ? "active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="card-body">
          <div className={`sdm-profile-tab-pane ${activeTab === "identitas" ? "" : "d-none"}`}>
            <DetailSection>
              <DetailField label="NIK" value={item?.nik} size={3} />
              <DetailField label="No. KK" value={item?.no_kk} size={3} />
              <DetailField label="Nama Lengkap" value={fullName} size={6} />
              <DetailField label="Tempat, Tanggal Lahir" value={birthPlaceDate} size={3} />
              <DetailField label="Usia" value={formatAge(item?.usia)} size={3} />
              <DetailField label="No. Akta Lahir" value={item?.nomor_akta_lahir} size={3} />
              <DetailField label="Tahun Akta Lahir" value={item?.tahun_akta_lahir} size={3} />
              <DetailField label="Jenis Kelamin" value={item?.jenis_kelamin_name} size={3} />
              <DetailField label="Golongan Darah" value={item?.golongan_darah} size={3} />
              <DetailField label="Agama" value={item?.agama_name} size={3} />
              <DetailField label="Kewarganegaraan" value={item?.kewarganegaraan} size={3} />
              <DetailField label="Status Perkawinan" value={item?.status_perkawinan_name} size={3} />
              <DetailField label="HP" value={item?.hp} size={3} />
              <DetailField label="Telepon" value={item?.telepon} size={3} />
              <DetailField label="Email" value={item?.email} size={3} />
              <DetailField label="Alamat" value={item?.alamat} size={6} />
              <DetailField label="Kab/Kota" value={item?.kota} size={3} />
              <DetailField label="Kode POS" value={item?.kode_pos} size={3} />
              <DetailField label="Rekening Bank" value={bankAccount} size={12} />
            </DetailSection>
            <SupportingDocumentsSection item={item} />
          </div>

          <div className={`sdm-profile-tab-pane ${activeTab === "pekerjaan" ? "" : "d-none"}`}>
            <DetailSection>
              <DetailField label="Pekerjaan" value={item?.pekerjaan} size={3} />
              <DetailField label="Jabatan" value={item?.jabatan_pekerjaan} size={3} />
              <DetailField label="Bidang Usaha" value={item?.bidang_usaha} size={3} />
              <DetailField label="Nama Tempat Kerja" value={item?.nama_tempat_kerja} size={3} />
              <DetailField label="Telepon Tempat Kerja" value={item?.telepon_tempat_kerja} size={3} />
              <DetailField label="Alamat Tempat Kerja" value={item?.alamat_tempat_kerja} size={6} />
              <DetailField label="Pendapatan" value={formatWholeNumber(item?.pendapatan)} size={3} />
              <DetailField label="Sumber Pendapatan" value={item?.sumber_pendapatan} size={3} />
              <DetailField label="NPWP" value={item?.npwp} size={3} />
            </DetailSection>
          </div>

          <div className={`sdm-profile-tab-pane ${activeTab === "orang_tua" ? "" : "d-none"}`}>
            <DetailSection>
              <DetailField label="Nama Ayah" value={item?.nama_ayah} size={3} />
              <DetailField label="Pekerjaan Ayah" value={item?.pekerjaan_ayah} size={3} />
              <DetailField label="Nama Ibu" value={item?.nama_ibu} size={3} />
              <DetailField label="Pekerjaan Ibu" value={item?.pekerjaan_ibu} size={3} />
              <DetailField label="Alamat Orang Tua" value={item?.alamat_orang_tua} size={12} />
            </DetailSection>
          </div>

          <div className={`sdm-profile-tab-pane ${activeTab === "asuransi" ? "" : "d-none"}`}>
            <DetailSection>
              <DetailField label="No. BPJS Kesehatan" value={item?.nomor_bpjs_kesehatan} size={3} />
              <DetailField label="Status BPJS Kesehatan" value={item?.status_bpjs_kesehatan} size={3} />
              <DetailField label="No. BPJS Ketenagakerjaan" value={item?.nomor_bpjs_ketenagakerjaan} size={3} />
              <DetailField label="Status BPJS Ketenagakerjaan" value={item?.status_bpjs_ketenagakerjaan} size={3} />
              <DetailField label="Asuransi Lainnya" value={item?.asuransi_lainnya} size={6} />
              <DetailField label="Nomor Asuransi Lainnya" value={item?.nomor_asuransi_lainnya} size={6} />
            </DetailSection>
          </div>

          <div className={`sdm-profile-tab-pane ${activeTab === "pendidikan" ? "" : "d-none"}`}>
            <DetailSection>
              <div className="col-12">
                {educationHistory.length < 1 ? (
                  <div className="text-muted">Riwayat pendidikan belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Jenjang Pendidikan</th>
                          <th>Program Studi</th>
                          <th>Nama Institusi</th>
                          <th>Alamat</th>
                          <th style={{ width: 90 }} className="text-center">Tahun Lulus</th>
                          <th style={{ width: 120 }} className="text-center">Bulan Lulus</th>
                        </tr>
                      </thead>
                      <tbody>
                        {educationHistory.map((row, index) => (
                          <tr key={row?.id_riwayat_pendidikan || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(row?.jenjang_pendidikan)}</td>
                            <td>{detailValue(row?.program_studi)}</td>
                            <td>{detailValue(row?.nama_institusi)}</td>
                            <td>{detailValue(row?.alamat)}</td>
                            <td className="text-center">{detailValue(row?.tahun_lulus)}</td>
                            <td className="text-center">{detailValue(row?.bulan_lulus_name)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>

          <div className={`sdm-profile-tab-pane ${activeTab === "prestasi" ? "" : "d-none"}`}>
            <DetailSection>
              <div className="col-12">
                {capaianBaseUrl ? (
                  <PersonalAchievementTable
                    baseUrl={`${capaianBaseUrl}/prestasi`}
                    optionsBaseUrl={`${capaianBaseUrl}/capaian`}
                    peranOptionsUrl={`${capaianBaseUrl}/capaian/peran-options`}
                  />
                ) : achievementHistory.length < 1 ? (
                  <div className="text-muted">Prestasi belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Peran</th>
                          <th>Nama Pertandingan</th>
                          <th>Cabor</th>
                          <th>Nomor Pertandingan</th>
                          <th>Tingkat</th>
                          <th>Hasil</th>
                          <th style={{ width: 70 }} className="text-center">Skor</th>
                          <th style={{ width: 120 }} className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {achievementHistory.map((row, index) => (
                          <tr key={row?.id_personal_prestasi || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(row?.peran_name)}</td>
                            <td>{detailValue(row?.nama_pertandingan)}</td>
                            <td>{detailValue(row?.cabor_name)}</td>
                            <td>{detailValue(row?.nomor_pertandingan_name)}</td>
                            <td>{detailValue(row?.tingkat_name)}</td>
                            <td>{detailValue(row?.hasil_name)}</td>
                            <td className="text-center">{detailValue(row?.skor)}</td>
                            <td className="text-center">{detailValue(row?.status_name)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>

          <div className={`sdm-profile-tab-pane ${activeTab === "sertifikasi" ? "" : "d-none"}`}>
            <DetailSection>
              <div className="col-12">
                {capaianBaseUrl ? (
                  <PersonalCertificationTable
                    baseUrl={`${capaianBaseUrl}/sertifikasi`}
                    peranOptionsUrl={`${capaianBaseUrl}/capaian/peran-options`}
                  />
                ) : certificationHistory.length < 1 ? (
                  <div className="text-muted">Sertifikasi belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Peran</th>
                          <th>Nama Sertifikasi</th>
                          <th>Jenis</th>
                          <th>Tingkat</th>
                          <th>Lembaga Penyelenggara</th>
                          <th>Nomor</th>
                          <th style={{ width: 120 }} className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {certificationHistory.map((row, index) => (
                          <tr key={row?.id_personal_sertifikasi || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(row?.peran_name)}</td>
                            <td>{detailValue(row?.nama_sertifikasi)}</td>
                            <td>{detailValue(row?.jenis_sertifikasi_name)}</td>
                            <td>{detailValue(row?.tingkat_name)}</td>
                            <td>{detailValue(row?.lembaga_penyelenggara)}</td>
                            <td>{detailValue(row?.nomor)}</td>
                            <td className="text-center">{detailValue(row?.status_name)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>

          <div className={`sdm-profile-tab-pane ${activeTab === "keanggotaan" ? "" : "d-none"}`}>
            <DetailSection>
              <div className="col-12">
                {membershipHistory.length < 1 ? (
                  <div className="text-muted">Riwayat keanggotaan belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Peran</th>
                          <th>No. Induk</th>
                          <th>Cabor</th>
                          <th>Induk Organisasi Cabor</th>
                          <th>Klub Olahraga</th>
                          <th className="text-center">Periode</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {membershipHistory.map((row, index) => (
                          <tr key={row?.id_keanggotaan || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(row?.peran_name)}</td>
                            <td>{detailValue(row?.nomor_induk)}</td>
                            <td>{detailValue(row?.cabor_name)}</td>
                            <td>{detailValue(row?.induk_organisasi_cabor_name)}</td>
                            <td>{detailValue(row?.klub_olahraga_name)}</td>
                            <td className="text-center">{detailValue(renderPeriod(null, null, row))}</td>
                            <td className="text-center">{boolText(row?.aktif)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>

          <div className={`sdm-profile-tab-pane ${activeTab === "riwayat" ? "" : "d-none"}`}>
            <DetailSection>
              <div className="col-12">
                {history.length < 1 ? (
                  <div className="text-muted">Riwayat organisasi belum tersedia.</div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th style={{ width: 50 }} className="text-center">No</th>
                          <th>Organisasi</th>
                          <th>Jabatan</th>
                          <th className="text-center">Periode</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {history.map((row, index) => (
                          <tr key={row?.id_pengurus_organisasi || index}>
                            <td className="text-center">{index + 1}</td>
                            <td>{detailValue(row?.organisasi_name)}</td>
                            <td>{detailValue(row?.jabatan)}</td>
                            <td className="text-center">{detailValue(renderPeriod(null, null, row))}</td>
                            <td className="text-center">{boolText(row?.aktif)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </DetailSection>
          </div>
        </div>
      </div>
    </div>
  );
}

const pdfStyles = StyleSheet.create({
  title: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    textAlign: "center",
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 10,
    fontFamily: "Helvetica-Bold",
    marginBottom: 6,
    marginTop: 2,
  },
  section: {
    marginBottom: 6,
    padding: 4,
  },
  lastSection: {
    marginBottom: 0,
  },
  contentRow: {
    flexDirection: "row",
    width: 515,
  },
  rowsWrap: {
    width: 385,
    paddingRight: 10,
  },
  row: {
    flexDirection: "row",
    width: 385,
    marginBottom: 2.5,
  },
  fullRow: {
    flexDirection: "row",
    width: 515,
    marginBottom: 2.5,
  },
  label: {
    width: 138,
    fontSize: 8.5,
    fontFamily: "Helvetica-Bold",
    lineHeight: 1.25,
  },
  colon: {
    width: 8,
    fontSize: 8.5,
    textAlign: "center",
    lineHeight: 1.25,
  },
  value: {
    width: 239,
    fontSize: 8.5,
    lineHeight: 1.25,
  },
  fullValue: {
    width: 349,
    fontSize: 8.5,
    lineHeight: 1.25,
  },
  photoWrap: {
    width: 130,
    alignItems: "flex-end",
    paddingTop: 0,
    padding: 4,
  },
  photo: {
    width: 82,
    height: 104,
    objectFit: "cover",
    borderWidth: 1,
    borderColor: "#333",
  },
  photoPlaceholder: {
    width: 82,
    height: 104,
    borderWidth: 1,
    borderColor: "#333",
    alignItems: "center",
    justifyContent: "center",
  },
  muted: {
    color: "#666",
    fontSize: 8,
  },
  historyTable: {
    width: 515,
  },
  historyHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#f1f3f5",
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: "#999",
  },
  historyRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderColor: "#999",
  },
  historyCell: {
    fontSize: 7,
    padding: 3,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#999",
  },
  historyHeaderCell: {
    fontSize: 7,
    fontFamily: "Helvetica-Bold",
    padding: 3,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#999",
  },
});

const pengurusHistoryColWidth = {
  no: 28,
  organisasi: 205,
  jabatan: 125,
  periode: 100,
  status: 57,
};

function pdfValue(value) {
  return detailValue(value);
}

function PdfRow({ label, value, full = false }) {
  return (
    <View style={full ? pdfStyles.fullRow : pdfStyles.row}>
      <Text style={pdfStyles.label}>{label}</Text>
      <Text style={pdfStyles.colon}>:</Text>
      <Text style={full ? pdfStyles.fullValue : pdfStyles.value}>{pdfValue(value)}</Text>
    </View>
  );
}

function PdfSection({ title, children, last = false }) {
  return (
    <View style={[pdfStyles.section, last ? pdfStyles.lastSection : null]}>
      <Text style={pdfStyles.sectionTitle}>{title}</Text>
      {children}
    </View>
  );
}

function PdfHistoryCell({ children, width, header = false, center = false }) {
  return (
    <Text
      style={[
        header ? pdfStyles.historyHeaderCell : pdfStyles.historyCell,
        { width, textAlign: center ? "center" : "left" },
      ]}
    >
      {children}
    </Text>
  );
}

function PdfEducationHistory({ items = [] }) {
  return (
    <PdfSection title="Pendidikan">
      {items.length < 1 ? (
        <Text style={pdfStyles.muted}>Riwayat pendidikan belum tersedia.</Text>
      ) : (
        <View style={pdfStyles.historyTable}>
          <View style={pdfStyles.historyHeaderRow}>
            <PdfHistoryCell width={24} header center>No</PdfHistoryCell>
            <PdfHistoryCell width={86} header>Jenjang Pendidikan</PdfHistoryCell>
            <PdfHistoryCell width={76} header>Program Studi</PdfHistoryCell>
            <PdfHistoryCell width={112} header>Nama Institusi</PdfHistoryCell>
            <PdfHistoryCell width={116} header>Alamat</PdfHistoryCell>
            <PdfHistoryCell width={50} header center>Tahun</PdfHistoryCell>
            <PdfHistoryCell width={51} header center>Bulan</PdfHistoryCell>
          </View>
          {items.map((row, index) => (
            <View key={row?.id_riwayat_pendidikan || index} style={pdfStyles.historyRow} wrap={false}>
              <PdfHistoryCell width={24} center>{index + 1}</PdfHistoryCell>
              <PdfHistoryCell width={86}>{pdfValue(row?.jenjang_pendidikan)}</PdfHistoryCell>
              <PdfHistoryCell width={76}>{pdfValue(row?.program_studi)}</PdfHistoryCell>
              <PdfHistoryCell width={112}>{pdfValue(row?.nama_institusi)}</PdfHistoryCell>
              <PdfHistoryCell width={116}>{pdfValue(row?.alamat)}</PdfHistoryCell>
              <PdfHistoryCell width={50} center>{pdfValue(row?.tahun_lulus)}</PdfHistoryCell>
              <PdfHistoryCell width={51} center>{pdfValue(row?.bulan_lulus_name)}</PdfHistoryCell>
            </View>
          ))}
        </View>
      )}
    </PdfSection>
  );
}

function PdfAchievementHistory({ items = [] }) {
  return (
    <PdfSection title="Prestasi">
      {items.length < 1 ? (
        <Text style={pdfStyles.muted}>Prestasi belum tersedia.</Text>
      ) : (
        <View style={pdfStyles.historyTable}>
          <View style={pdfStyles.historyHeaderRow}>
            <PdfHistoryCell width={24} header center>No</PdfHistoryCell>
            <PdfHistoryCell width={56} header>Peran</PdfHistoryCell>
            <PdfHistoryCell width={108} header>Nama Pertandingan</PdfHistoryCell>
            <PdfHistoryCell width={70} header>Cabor</PdfHistoryCell>
            <PdfHistoryCell width={88} header>Nomor Pertandingan</PdfHistoryCell>
            <PdfHistoryCell width={63} header>Tingkat</PdfHistoryCell>
            <PdfHistoryCell width={58} header>Hasil</PdfHistoryCell>
            <PdfHistoryCell width={22} header center>Skor</PdfHistoryCell>
            <PdfHistoryCell width={26} header center>Status</PdfHistoryCell>
          </View>
          {items.map((row, index) => (
            <View key={row?.id_personal_prestasi || index} style={pdfStyles.historyRow} wrap={false}>
              <PdfHistoryCell width={24} center>{index + 1}</PdfHistoryCell>
              <PdfHistoryCell width={56}>{pdfValue(row?.peran_name)}</PdfHistoryCell>
              <PdfHistoryCell width={108}>{pdfValue(row?.nama_pertandingan)}</PdfHistoryCell>
              <PdfHistoryCell width={70}>{pdfValue(row?.cabor_name)}</PdfHistoryCell>
              <PdfHistoryCell width={88}>{pdfValue(row?.nomor_pertandingan_name)}</PdfHistoryCell>
              <PdfHistoryCell width={63}>{pdfValue(row?.tingkat_name)}</PdfHistoryCell>
              <PdfHistoryCell width={58}>{pdfValue(row?.hasil_name)}</PdfHistoryCell>
              <PdfHistoryCell width={22} center>{pdfValue(row?.skor)}</PdfHistoryCell>
              <PdfHistoryCell width={26} center>{pdfValue(row?.status_name)}</PdfHistoryCell>
            </View>
          ))}
        </View>
      )}
    </PdfSection>
  );
}

function PdfCertificationHistory({ items = [] }) {
  return (
    <PdfSection title="Sertifikasi">
      {items.length < 1 ? (
        <Text style={pdfStyles.muted}>Sertifikasi belum tersedia.</Text>
      ) : (
        <View style={pdfStyles.historyTable}>
          <View style={pdfStyles.historyHeaderRow}>
            <PdfHistoryCell width={24} header center>No</PdfHistoryCell>
            <PdfHistoryCell width={62} header>Peran</PdfHistoryCell>
            <PdfHistoryCell width={118} header>Nama Sertifikasi</PdfHistoryCell>
            <PdfHistoryCell width={72} header>Jenis</PdfHistoryCell>
            <PdfHistoryCell width={72} header>Tingkat</PdfHistoryCell>
            <PdfHistoryCell width={95} header>Lembaga</PdfHistoryCell>
            <PdfHistoryCell width={46} header>Nomor</PdfHistoryCell>
            <PdfHistoryCell width={26} header center>Status</PdfHistoryCell>
          </View>
          {items.map((row, index) => (
            <View key={row?.id_personal_sertifikasi || index} style={pdfStyles.historyRow} wrap={false}>
              <PdfHistoryCell width={24} center>{index + 1}</PdfHistoryCell>
              <PdfHistoryCell width={62}>{pdfValue(row?.peran_name)}</PdfHistoryCell>
              <PdfHistoryCell width={118}>{pdfValue(row?.nama_sertifikasi)}</PdfHistoryCell>
              <PdfHistoryCell width={72}>{pdfValue(row?.jenis_sertifikasi_name)}</PdfHistoryCell>
              <PdfHistoryCell width={72}>{pdfValue(row?.tingkat_name)}</PdfHistoryCell>
              <PdfHistoryCell width={95}>{pdfValue(row?.lembaga_penyelenggara)}</PdfHistoryCell>
              <PdfHistoryCell width={46}>{pdfValue(row?.nomor)}</PdfHistoryCell>
              <PdfHistoryCell width={26} center>{pdfValue(row?.status_name)}</PdfHistoryCell>
            </View>
          ))}
        </View>
      )}
    </PdfSection>
  );
}

function PdfMembershipHistory({ items = [] }) {
  return (
    <PdfSection title="Keanggotaan">
      {items.length < 1 ? (
        <Text style={pdfStyles.muted}>Riwayat keanggotaan belum tersedia.</Text>
      ) : (
        <View style={pdfStyles.historyTable}>
          <View style={pdfStyles.historyHeaderRow}>
            <PdfHistoryCell width={24} header center>No</PdfHistoryCell>
            <PdfHistoryCell width={62} header>Peran</PdfHistoryCell>
            <PdfHistoryCell width={72} header>No. Induk</PdfHistoryCell>
            <PdfHistoryCell width={75} header>Cabor</PdfHistoryCell>
            <PdfHistoryCell width={105} header>Induk Organisasi Cabor</PdfHistoryCell>
            <PdfHistoryCell width={86} header>Klub Olahraga</PdfHistoryCell>
            <PdfHistoryCell width={62} header center>Periode</PdfHistoryCell>
            <PdfHistoryCell width={29} header center>Status</PdfHistoryCell>
          </View>
          {items.map((row, index) => (
            <View key={row?.id_keanggotaan || index} style={pdfStyles.historyRow} wrap={false}>
              <PdfHistoryCell width={24} center>{index + 1}</PdfHistoryCell>
              <PdfHistoryCell width={62}>{pdfValue(row?.peran_name)}</PdfHistoryCell>
              <PdfHistoryCell width={72}>{pdfValue(row?.nomor_induk)}</PdfHistoryCell>
              <PdfHistoryCell width={75}>{pdfValue(row?.cabor_name)}</PdfHistoryCell>
              <PdfHistoryCell width={105}>{pdfValue(row?.induk_organisasi_cabor_name)}</PdfHistoryCell>
              <PdfHistoryCell width={86}>{pdfValue(row?.klub_olahraga_name)}</PdfHistoryCell>
              <PdfHistoryCell width={62} center>{pdfValue(renderPeriod(null, null, row))}</PdfHistoryCell>
              <PdfHistoryCell width={29} center>{boolText(row?.aktif)}</PdfHistoryCell>
            </View>
          ))}
        </View>
      )}
    </PdfSection>
  );
}

function PengurusProfilePDF({ item }) {
  const fullName = formatFullName(item);
  const birthPlaceDate = [item?.tempat_lahir, formatDate(item?.tanggal_lahir)].filter(Boolean).join(", ");
  const aktaLahir = [item?.nomor_akta_lahir ? `Nomor ${item.nomor_akta_lahir}` : "", item?.tahun_akta_lahir ? `Tahun ${item.tahun_akta_lahir}` : ""]
    .filter(Boolean)
    .join(" ");
  const phoneLabel = [item?.hp, item?.telepon].filter(Boolean).join(" / ");
  const photoUrl = item?.photo_data_url || "";
  const pendapatan = item?.pendapatan ? `Rp ${formatWholeNumber(item.pendapatan)}` : "";
  const history = pengurusHistoryItems(item);
  const membershipHistory = membershipHistoryItems(item);
  const educationHistory = educationHistoryItems(item);
  const achievementHistory = achievementHistoryItems(item);
  const certificationHistory = certificationHistoryItems(item);

  return (
    <View>
      <Text style={pdfStyles.title}>PROFIL PENGURUS</Text>
      <PdfSection title="Data Pribadi">
        <View style={pdfStyles.contentRow}>
          <View style={pdfStyles.rowsWrap}>
            <PdfRow label="Organisasi" value={item?.organisasi_name} />
            <PdfRow label="Jabatan" value={item?.jabatan} />
            <PdfRow label="Periode" value={renderPeriod(null, null, item)} />
            <PdfRow label="NIK" value={item?.nik} />
            <PdfRow label="No. KK" value={item?.no_kk} />
            <PdfRow label="Nama Lengkap" value={fullName} />
            <PdfRow label="Tempat, Tanggal Lahir" value={birthPlaceDate} />
            <PdfRow label="Akta Lahir" value={aktaLahir} />
            <PdfRow label="Jenis Kelamin" value={item?.jenis_kelamin_name} />
          </View>
          <View style={pdfStyles.photoWrap}>
            {photoUrl ? (
              <PdfImage
                src={photoUrl}
                style={pdfStyles.photo}
                cache={false}
              />
            ) : (
              <View style={pdfStyles.photoPlaceholder}>
                <Text style={pdfStyles.muted}>Foto</Text>
              </View>
            )}
          </View>
        </View>
        <PdfRow label="Usia" value={formatAge(item?.usia)} full />
        <PdfRow label="Golongan Darah" value={item?.golongan_darah} full />
        <PdfRow label="Agama" value={item?.agama_name} full />
        <PdfRow label="Kewarganegaraan" value={item?.kewarganegaraan} full />
        <PdfRow label="Status Perkawinan" value={item?.status_perkawinan_name} full />
        <PdfRow label="HP/Telepon" value={phoneLabel} full />
        <PdfRow label="Email" value={item?.email} full />
        <PdfRow label="Alamat" value={item?.alamat} full />
        <PdfRow label="Kab/Kota" value={item?.kota} full />
        <PdfRow label="Kode POS" value={item?.kode_pos} full />
        <PdfRow label="Rekening Bank" value={formatBankAccount(item)} full />
      </PdfSection>

      <PdfSection title="Data Pekerjaan">
        <PdfRow label="Pekerjaan" value={item?.pekerjaan} full />
        <PdfRow label="Jabatan di Tempat Kerja" value={item?.jabatan_pekerjaan} full />
        <PdfRow label="Bidang Usaha Tempat Kerja" value={item?.bidang_usaha} full />
        <PdfRow label="Nama Tempat Kerja" value={item?.nama_tempat_kerja} full />
        <PdfRow label="Telepon Tempat Kerja" value={item?.telepon_tempat_kerja} full />
        <PdfRow label="Alamat Tempat Kerja" value={item?.alamat_tempat_kerja} full />
        <PdfRow label="Pendapatan" value={pendapatan} full />
        <PdfRow label="Sumber Pendapatan" value={item?.sumber_pendapatan} full />
        <PdfRow label="NPWP" value={item?.npwp} full />
      </PdfSection>

      <PdfSection title="Data Orang Tua">
        <PdfRow label="Nama Ayah" value={item?.nama_ayah} full />
        <PdfRow label="Pekerjaan Ayah" value={item?.pekerjaan_ayah} full />
        <PdfRow label="Nama Ibu" value={item?.nama_ibu} full />
        <PdfRow label="Pekerjaan Ibu" value={item?.pekerjaan_ibu} full />
        <PdfRow label="Alamat Orang Tua" value={item?.alamat_orang_tua} full />
      </PdfSection>

      <PdfSection title="Asuransi">
        <PdfRow label="No. BPJS Kesehatan" value={item?.nomor_bpjs_kesehatan} full />
        <PdfRow label="Status BPJS Kesehatan" value={item?.status_bpjs_kesehatan} full />
        <PdfRow label="No. BPJS Ketenagakerjaan" value={item?.nomor_bpjs_ketenagakerjaan} full />
        <PdfRow label="Status BPJS Ketenagakerjaan" value={item?.status_bpjs_ketenagakerjaan} full />
        <PdfRow label="Asuransi Lainnya" value={item?.asuransi_lainnya} full />
        <PdfRow label="Nomor Asuransi Lainnya" value={item?.nomor_asuransi_lainnya} full />
      </PdfSection>

      <PdfEducationHistory items={educationHistory} />
      <PdfAchievementHistory items={achievementHistory} />
      <PdfCertificationHistory items={certificationHistory} />
      <PdfMembershipHistory items={membershipHistory} />

      <PdfSection title="Riwayat Organisasi" last>
        {history.length < 1 ? (
          <Text style={pdfStyles.muted}>Riwayat organisasi belum tersedia.</Text>
        ) : (
          <View style={pdfStyles.historyTable}>
            <View style={pdfStyles.historyHeaderRow}>
              <PdfHistoryCell width={pengurusHistoryColWidth.no} header center>No</PdfHistoryCell>
              <PdfHistoryCell width={pengurusHistoryColWidth.organisasi} header>Organisasi</PdfHistoryCell>
              <PdfHistoryCell width={pengurusHistoryColWidth.jabatan} header>Jabatan</PdfHistoryCell>
              <PdfHistoryCell width={pengurusHistoryColWidth.periode} header center>Periode</PdfHistoryCell>
              <PdfHistoryCell width={pengurusHistoryColWidth.status} header center>Status</PdfHistoryCell>
            </View>
            {history.map((row, index) => (
              <View key={row?.id_pengurus_organisasi || index} style={pdfStyles.historyRow} wrap={false}>
                <PdfHistoryCell width={pengurusHistoryColWidth.no} center>{index + 1}</PdfHistoryCell>
                <PdfHistoryCell width={pengurusHistoryColWidth.organisasi}>{pdfValue(row?.organisasi_name)}</PdfHistoryCell>
                <PdfHistoryCell width={pengurusHistoryColWidth.jabatan}>{pdfValue(row?.jabatan)}</PdfHistoryCell>
                <PdfHistoryCell width={pengurusHistoryColWidth.periode} center>{pdfValue(renderPeriod(null, null, row))}</PdfHistoryCell>
                <PdfHistoryCell width={pengurusHistoryColWidth.status} center>{boolText(row?.aktif)}</PdfHistoryCell>
              </View>
            ))}
          </View>
        )}
      </PdfSection>
    </View>
  );
}

export default function PengurusManagementPage() {
  const { user } = useAuth();
  const [, , , axiosFetch] = useAxiosFunction();
  const [, , loadingProfilePDF, axiosFetchProfilePDF] = useAxiosFunction();
  const [showMsg, confirmMsg] = useMsgBox();
  const [getProfilePDFViewer, toggleProfilePDFViewer] = usePDFRenderer();
  const [profilePDFData, setProfilePDFData] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const lookupKeyRef = useRef("");
  const lookupTimerRef = useRef(null);
  const [addLookup, setAddLookup] = useState({
    status: "idle",
    exists: false,
    registered: false,
    message: "Isi NIK 16 digit untuk memulai pengecekan data.",
  });

  const [jabatanResp] = useAxios({ method: "GET", url: "/references/jabatan-pengurus", auth: true });
  const [jenisKelaminResp] = useAxios({ method: "GET", url: "/references/jenis-kelamin", auth: true });
  const [agamaResp] = useAxios({ method: "GET", url: "/references/agama", auth: true });
  const [statusPerkawinanResp] = useAxios({ method: "GET", url: "/references/status-perkawinan", auth: true });
  const [pekerjaanResp] = useAxios({ method: "GET", url: "/references/pekerjaan", auth: true });
  const [sumberPendapatanResp] = useAxios({ method: "GET", url: "/references/sumber-pendapatan", auth: true });

	const groupName = String(user?.groupname || "").toUpperCase();
  const isAdminKoni = ["ADMIN_KONI_KABKOTA", "ADMIN_KONI_PROVINSI"].includes(groupName);
	const canManage = hasPermission(user, "personal.manage");
	const canDelete = canManage && !["ADMIN_CABOR", "ADMIN_KLUB"].includes(groupName);
  const jabatanOptions = Array.isArray(jabatanResp?.data) ? jabatanResp.data : [];
  const jenisKelaminOptions = Array.isArray(jenisKelaminResp?.data) ? jenisKelaminResp.data : [];
  const agamaOptions = Array.isArray(agamaResp?.data) ? agamaResp.data : [];
  const statusPerkawinanOptions = Array.isArray(statusPerkawinanResp?.data) ? statusPerkawinanResp.data : [];
  const pekerjaanOptions = Array.isArray(pekerjaanResp?.data) ? pekerjaanResp.data : [];
  const sumberPendapatanOptions = Array.isArray(sumberPendapatanResp?.data) ? sumberPendapatanResp.data : [];
  const golonganDarahOptions = [
    { id: "A", nama: "A" },
    { id: "B", nama: "B" },
    { id: "AB", nama: "AB" },
    { id: "O", nama: "O" },
  ];
  const statusBPJSOptions = [
    { id: "Aktif", nama: "Aktif" },
    { id: "Nonaktif", nama: "Nonaktif" },
  ];
  const statusAktifOptions = [
    { id: "true", nama: "Aktif" },
    { id: "false", nama: "Nonaktif" },
  ];

  const personalFields = [
    "id_personal",
    "no_kk",
    "gelar_depan",
    "nama_lengkap",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "id_jenis_kelamin",
    "golongan_darah",
    "id_agama",
    "kewarganegaraan",
    "id_status_perkawinan",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "file_kartu_keluarga",
    "file_ktp",
    "file_akta_lahir",
    "file_rekening_bank",
    "photo_url",
    "pekerjaan",
    "jabatan_pekerjaan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
  ];
  const lockedUntilLookupFields = [
    ...personalFields,
    "jabatan_pengurus",
    "tanggal_mulai",
  ];

  const updateAdminStatus = async (item, checked) => {
    const id = item?.id_pengurus_organisasi || "";
    if (!id) return;
    const name = item?.nama_lengkap || "pengurus ini";
    const actionText = checked ? "menjadikan" : "membatalkan";
    const ok = await confirmMsg(
      "Konfirmasi",
      `Apakah Anda yakin akan ${actionText} ${name} sebagai Admin Organisasi?`,
    );
    if (ok !== true) {
      setReloadKey((current) => current + 1);
      return;
    }
    const resp = await axiosFetch({
      method: "PUT",
      url: `/organisasi-pengurus/${id}/admin`,
      auth: true,
      data: { is_admin_organisasi: Boolean(checked) },
    });
    if (resp?.data?.success === true) {
      await showMsg("Informasi", "Status admin organisasi berhasil disimpan.");
      setReloadKey((current) => current + 1);
      return;
    }
    await showMsg("Error", resp?.message || "Status admin gagal disimpan.");
    setReloadKey((current) => current + 1);
  };

  const coltype = {
    id_pengurus_organisasi: { type: "hidden", name: "id_pengurus_organisasi" },
    id_personal: { type: "hidden", name: "id_personal" },
    nik: {
      label: "NIK",
      type: "text",
      name: "nik",
      validation: RuleRequired,
      colsize: 6,
      width: 115,
      align: "center",
    },
    no_kk: {
      label: "No. KK",
      type: "text",
      name: "no_kk",
      validation: RuleRequired,
      colsize: 3,
      width: 130,
      align: "center",
    },
    nama_lengkap: {
      label: "Nama",
      type: "text",
      name: "nama_lengkap",
      validation: RuleRequired,
      colsize: 3,
      width: 240,
      renderFunc: (_value, _column, item) => formatFullName(item),
      renderExportFunc: (_value, _column, item) => formatFullName(item),
    },
    jabatan: {
      label: "Jabatan",
      type: "text",
      name: "jabatan",
      width: 160,
      pdfwidth: 15,
    },
    gelar_depan: { label: "Gelar Depan", type: "text", name: "gelar_depan", colsize: 3 },
    gelar_belakang: { label: "Gelar Belakang", type: "text", name: "gelar_belakang", colsize: 3 },
    tempat_lahir: {
      label: "Tempat Lahir",
      type: "text",
      name: "tempat_lahir",
      validation: RuleRequired,
      colsize: 3,
    },
    tanggal_lahir: {
      label: "Tanggal Lahir",
      type: "date",
      name: "tanggal_lahir",
      validation: RuleRequired,
      colsize: 3,
    },
    nomor_akta_lahir: { label: "Nomor Akta Lahir", type: "text", name: "nomor_akta_lahir", colsize: 3 },
    tahun_akta_lahir: { label: "Tahun Akta Lahir", type: "number", name: "tahun_akta_lahir", colsize: 3 },
    id_jenis_kelamin: {
      label: "Jenis Kelamin",
      type: "dropdown",
      name: "id_jenis_kelamin",
      validation: RuleRequired,
      colsize: 3,
      options: jenisKelaminOptions,
      valueId: "id_jenis_kelamin",
      labelId: "nama",
      placeholder: "-Pilih Jenis Kelamin-",
    },
    golongan_darah: {
      label: "Golongan Darah",
      type: "dropdown",
      name: "golongan_darah",
      validation: RuleRequired,
      colsize: 3,
      options: golonganDarahOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Pilih Golongan Darah-",
    },
    id_agama: {
      label: "Agama",
      type: "dropdown",
      name: "id_agama",
      validation: RuleRequired,
      colsize: 3,
      options: agamaOptions,
      valueId: "id_agama",
      labelId: "nama",
      placeholder: "-Pilih Agama-",
    },
    kewarganegaraan: {
      label: "Kewarganegaraan",
      type: "text",
      name: "kewarganegaraan",
      validation: RuleRequired,
      colsize: 3,
    },
    id_status_perkawinan: {
      label: "Status Perkawinan",
      type: "dropdown",
      name: "id_status_perkawinan",
      validation: RuleRequired,
      colsize: 3,
      options: statusPerkawinanOptions,
      valueId: "id_status_perkawinan",
      labelId: "nama",
      placeholder: "-Pilih Status Perkawinan-",
    },
    hp: { label: "HP", type: "text", name: "hp", validation: RuleRequired, colsize: 3, width: 85, align: "center" },
    telepon: { label: "Telepon", type: "text", name: "telepon", colsize: 3 },
    email: { label: "Email", type: "email", name: "email", validation: RuleRequired, colsize: 3, width: 190 },
    alamat: { label: "Alamat", type: "textarea", name: "alamat", validation: RuleRequired, colsize: 6 },
    kota: { label: "Kab/Kota", type: "text", name: "kota", validation: RuleRequired, colsize: 3 },
    kode_pos: { label: "Kode POS", type: "text", name: "kode_pos", validation: RuleRequired, colsize: 3 },
    div_pekerjaan: { type: "divider", label: "Data Pekerjaan" },
    pekerjaan: {
      label: "Pekerjaan",
      type: "dropdowndinamis",
      name: "pekerjaan",
      colsize: 3,
      options: pekerjaanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Pekerjaan-",
      add_config: addSimpleReferenceConfig("Tambah Pekerjaan", "/references/pekerjaan", "id_pekerjaan", "Nama Pekerjaan"),
    },
    jabatan_pekerjaan: {
      label: "Jabatan di Tempat Kerja",
      type: "text",
      name: "jabatan_pekerjaan",
      colsize: 3,
    },
    bidang_usaha: { label: "Bidang Usaha Tempat Kerja", type: "text", name: "bidang_usaha", colsize: 3 },
    nama_tempat_kerja: { label: "Nama Tempat Kerja", type: "text", name: "nama_tempat_kerja", colsize: 3 },
    telepon_tempat_kerja: { label: "Telepon Tempat Kerja", type: "text", name: "telepon_tempat_kerja", colsize: 3 },
    alamat_tempat_kerja: { label: "Alamat Tempat Kerja", type: "textarea", name: "alamat_tempat_kerja", colsize: 6 },
    pendapatan: {
      label: "Pendapatan",
      type: "number",
      name: "pendapatan",
      colsize: 3,
      renderExportFunc: (val) => formatWholeNumber(val),
    },
    sumber_pendapatan: {
      label: "Sumber Pendapatan",
      type: "dropdowndinamis",
      name: "sumber_pendapatan",
      colsize: 3,
      options: sumberPendapatanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Sumber Pendapatan-",
      add_config: addSimpleReferenceConfig("Tambah Sumber Pendapatan", "/references/sumber-pendapatan", "id_sumber_pendapatan", "Nama Sumber Pendapatan"),
    },
    npwp: { label: "NPWP", type: "text", name: "npwp", colsize: 3 },
    div_orang_tua: { type: "divider", label: "Data Orang Tua" },
    nama_ayah: { label: "Nama Ayah", type: "text", name: "nama_ayah", validation: RuleRequired, colsize: 3 },
    pekerjaan_ayah: {
      label: "Pekerjaan Ayah",
      type: "dropdowndinamis",
      name: "pekerjaan_ayah",
      validation: RuleRequired,
      colsize: 3,
      options: pekerjaanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Pekerjaan Ayah-",
      add_config: addSimpleReferenceConfig("Tambah Pekerjaan Ayah", "/references/pekerjaan", "id_pekerjaan", "Nama Pekerjaan"),
    },
    nama_ibu: { label: "Nama Ibu", type: "text", name: "nama_ibu", validation: RuleRequired, colsize: 3 },
    pekerjaan_ibu: {
      label: "Pekerjaan Ibu",
      type: "dropdowndinamis",
      name: "pekerjaan_ibu",
      validation: RuleRequired,
      colsize: 3,
      options: pekerjaanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Pekerjaan Ibu-",
      add_config: addSimpleReferenceConfig("Tambah Pekerjaan Ibu", "/references/pekerjaan", "id_pekerjaan", "Nama Pekerjaan"),
    },
    alamat_orang_tua: {
      label: "Alamat Orang Tua",
      type: "textarea",
      name: "alamat_orang_tua",
      validation: RuleRequired,
      colsize: 12,
    },
    div_rekening: { type: "divider", label: "Rekening Bank" },
    nomor_rekening: { label: "Nomor Rekening", type: "text", name: "nomor_rekening", colsize: 4 },
    rekening_atas_nama: { label: "Rekening Atas Nama", type: "text", name: "rekening_atas_nama", colsize: 4 },
    bank: { label: "Bank", type: "text", name: "bank", colsize: 4 },
    div_bpjs: { type: "divider", label: "BPJS dan Asuransi" },
    nomor_bpjs_kesehatan: { label: "No. BPJS Kesehatan", type: "text", name: "nomor_bpjs_kesehatan", colsize: 3 },
    status_bpjs_kesehatan: {
      label: "Status BPJS Kesehatan",
      type: "dropdown",
      name: "status_bpjs_kesehatan",
      colsize: 3,
      options: statusBPJSOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Pilih Status-",
    },
    nomor_bpjs_ketenagakerjaan: { label: "No. BPJS Ketenagakerjaan", type: "text", name: "nomor_bpjs_ketenagakerjaan", colsize: 3 },
    status_bpjs_ketenagakerjaan: {
      label: "Status BPJS Ketenagakerjaan",
      type: "dropdown",
      name: "status_bpjs_ketenagakerjaan",
      colsize: 3,
      options: statusBPJSOptions,
      valueId: "id",
      labelId: "nama",
      placeholder: "-Pilih Status-",
    },
    asuransi_lainnya: { label: "Asuransi Lainnya", type: "text", name: "asuransi_lainnya", colsize: 6 },
    nomor_asuransi_lainnya: { label: "Nomor Asuransi Lainnya", type: "text", name: "nomor_asuransi_lainnya", colsize: 6 },
    jabatan_pengurus: {
      label: "Jabatan Pengurus",
      type: "dropdowndinamis",
      name: "jabatan_pengurus",
      validation: RuleRequired,
      colsize: 6,
      options: jabatanOptions,
      valueId: "nama",
      labelId: "nama",
      placeholder: "-Pilih Jabatan-",
      optionsUrl: "/references/jabatan-pengurus",
      optionsAuth: true,
      add_config: addSimpleReferenceConfig("Tambah Jabatan", "/references/jabatan-pengurus", "id_jabatan_pengurus", "Nama Jabatan"),
      width: 180,
    },
    is_admin_organisasi: {
      label: "Admin",
      name: "is_admin_organisasi",
      width: 55,
      align: "center",
      renderFunc: (val, col, item) => (
        <input
          type="checkbox"
          checked={Boolean(val)}
          disabled={!canManage || !item?.aktif}
          onChange={(event) => updateAdminStatus(item, event.target.checked)}
        />
      ),
      renderExportFunc: (val) => boolText(val),
    },
    tanggal_mulai: {
      label: "Tanggal Mulai Menjadi Pengurus",
      type: "date",
      name: "tanggal_mulai",
      validation: RuleRequired,
      colsize: 3,
      width: 120,
      renderFunc: (val) => showEmpty(formatDate(val)),
      renderExportFunc: (val) => showEmpty(formatDate(val)),
    },
    tanggal_selesai: {
      label: "Tanggal Selesai",
      type: "date",
      name: "tanggal_selesai",
      width: 120,
      renderFunc: (val) => showEmpty(formatDate(val)),
      renderExportFunc: (val) => showEmpty(formatDate(val)),
    },
    periode: { label: "Periode", width: 190, renderFunc: renderPeriod, renderExportFunc: renderPeriod },
    photo_url: {
      label: "Foto",
      type: "photodrop",
      name: "photo_url",
      colsize: 12,
      url: "/file/upload",
      data: { location: "personal" },
      allowedExtentions: ["jpg", "jpeg", "png"],
      caption: "Format jpg, jpeg, atau png.",
    },
    file_kartu_keluarga: {
      label: "Kartu Keluarga",
      type: "filedrop",
      name: "file_kartu_keluarga",
      colsize: 6,
      url: "/file/upload",
      data: { location: "personal-document" },
      allowedExtentions: ["jpg", "jpeg", "png", "pdf"],
    },
    file_ktp: {
      label: "KTP",
      type: "filedrop",
      name: "file_ktp",
      colsize: 6,
      url: "/file/upload",
      data: { location: "personal-document" },
      allowedExtentions: ["jpg", "jpeg", "png", "pdf"],
    },
    file_akta_lahir: {
      label: "Akta Kelahiran",
      type: "filedrop",
      name: "file_akta_lahir",
      colsize: 6,
      url: "/file/upload",
      data: { location: "personal-document" },
      allowedExtentions: ["jpg", "jpeg", "png", "pdf"],
    },
    file_rekening_bank: {
      label: "Rekening Bank",
      type: "filedrop",
      name: "file_rekening_bank",
      colsize: 6,
      url: "/file/upload",
      data: { location: "personal-document" },
      allowedExtentions: ["jpg", "jpeg", "png", "pdf"],
    },
    aktif: {
      label: "Status",
      type: "checkbox",
      name: "aktif",
      width: 70,
      align: "center",
      renderFunc: (val) => boolText(val),
      renderExportFunc: (val) => boolText(val),
    },
    action: { label: "Aksi", align: "center", width: 100 },
  };

  const formColumns = [
    "nik",
    "jabatan_pengurus",
    "no_kk",
    "nama_lengkap",
    "gelar_depan",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "id_jenis_kelamin",
    "golongan_darah",
    "id_agama",
    "kewarganegaraan",
    "id_status_perkawinan",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "div_pekerjaan",
    "pekerjaan",
    "jabatan_pekerjaan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "div_orang_tua",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "div_rekening",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "div_bpjs",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
    "tanggal_mulai",
    "photo_url",
    "file_kartu_keluarga",
    "file_ktp",
    "file_akta_lahir",
    "file_rekening_bank",
  ];
  const detailColumns = [
    "nik",
    "jabatan",
    "no_kk",
    "nama_lengkap",
    "gelar_depan",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "id_jenis_kelamin",
    "golongan_darah",
    "id_agama",
    "kewarganegaraan",
    "id_status_perkawinan",
    "hp",
    "telepon",
    "email",
    "alamat",
    "kota",
    "kode_pos",
    "div_pekerjaan",
    "pekerjaan",
    "jabatan_pekerjaan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "div_orang_tua",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "div_rekening",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "div_bpjs",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
    "tanggal_mulai",
    "tanggal_selesai",
    "aktif",
    "photo_url",
    "file_kartu_keluarga",
    "file_ktp",
    "file_akta_lahir",
    "file_rekening_bank",
  ];
  const listColumns = ["no", "nik", "nama_lengkap", "jabatan", "is_admin_organisasi", "hp", "periode", "aktif"];
  const xlsColumns = [
    "no",
    "nik",
    "nama_lengkap",
    "jabatan",
    "is_admin_organisasi",
    "hp",
    "email",
    "periode",
    "aktif",
    "no_kk",
    "gelar_depan",
    "gelar_belakang",
    "tempat_lahir",
    "tanggal_lahir",
    "nomor_akta_lahir",
    "tahun_akta_lahir",
    "id_jenis_kelamin",
    "golongan_darah",
    "id_agama",
    "kewarganegaraan",
    "id_status_perkawinan",
    "telepon",
    "alamat",
    "kota",
    "kode_pos",
    "pekerjaan",
    "jabatan_pekerjaan",
    "bidang_usaha",
    "nama_tempat_kerja",
    "telepon_tempat_kerja",
    "alamat_tempat_kerja",
    "pendapatan",
    "sumber_pendapatan",
    "npwp",
    "nama_ayah",
    "pekerjaan_ayah",
    "nama_ibu",
    "pekerjaan_ibu",
    "alamat_orang_tua",
    "nomor_rekening",
    "rekening_atas_nama",
    "bank",
    "nomor_bpjs_kesehatan",
    "status_bpjs_kesehatan",
    "nomor_bpjs_ketenagakerjaan",
    "status_bpjs_ketenagakerjaan",
    "asuransi_lainnya",
    "nomor_asuransi_lainnya",
    "tanggal_mulai",
    "tanggal_selesai",
  ];

  const isAddReadyToSave = addLookup.status === "new" || addLookup.status === "available";
  const editableLookupUploadFields = [
    "photo_url",
    "file_kartu_keluarga",
    "file_ktp",
    "file_akta_lahir",
    "file_rekening_bank",
  ];
  const addColtype = useMemo(() => {
    const locked = !isAddReadyToSave;
    const existingPersonalLocked = addLookup.status === "available";
    return Object.keys(coltype).reduce((result, key) => {
      const item = coltype[key];
      const personalLocked =
        existingPersonalLocked &&
        personalFields.includes(key) &&
        !editableLookupUploadFields.includes(key);
      result[key] = (locked && lockedUntilLookupFields.includes(key)) || personalLocked
        ? { ...item, disabled: true }
        : item;
      return result;
    }, {});
  }, [addLookup.status, coltype, editableLookupUploadFields, isAddReadyToSave]);

  const editColtype = useMemo(() => {
    return Object.keys(coltype).reduce((result, key) => {
      const item = coltype[key];
      result[key] = key === "nik" ? { ...item, disabled: true } : item;
      return result;
    }, {});
  }, [coltype]);

  const resetAddLookup = () => {
    lookupKeyRef.current = "";
    if (lookupTimerRef.current) {
      clearTimeout(lookupTimerRef.current);
      lookupTimerRef.current = null;
    }
    setAddLookup({
      status: "idle",
      exists: false,
      registered: false,
      message: "Isi NIK 16 digit untuk memulai pengecekan data.",
    });
  };

  const clearLookupFields = (setValue) => {
    [...personalFields, "jabatan_pengurus", "tanggal_mulai"].forEach((field) => setValue(field, ""));
  };

  const applyLookupResult = (setValue, result) => {
    const personal = result?.personal || {};
    personalFields.forEach((field) => {
      const sourceField = field === "jabatan_pekerjaan" ? "jabatan" : field;
      if (Object.prototype.hasOwnProperty.call(personal, sourceField)) {
        setValue(field, personal[sourceField] ?? "");
      }
    });
    if (result?.registered) {
      const pengurus = result?.pengurus || {};
      setValue("jabatan_pengurus", pengurus?.jabatan_pengurus || "");
      setValue("tanggal_mulai", pengurus?.tanggal_mulai || "");
      setAddLookup({
        status: "registered",
        exists: true,
        registered: true,
        message: "NIK ini sudah terdaftar sebagai pengurus pada organisasi aktif.",
      });
      return;
    }
    setAddLookup({
      status: "available",
      exists: true,
      registered: false,
      message: "Data personal ditemukan. Data personal tidak dapat diubah, silakan isi data pengurus.",
    });
  };

  const onAddFormChange = (setValue, form) => {
    const nikRaw = String(form?.nik || "");
    const nik = nikRaw.replace(/\D/g, "").slice(0, 16);
    if (nikRaw !== nik) {
      setValue("nik", nik);
      return;
    }
    const key = `pengurus|${nik}`;
    if (nik.length < 16) {
      if (lookupKeyRef.current !== key) {
        lookupKeyRef.current = key;
        clearLookupFields(setValue);
        setAddLookup({
          status: "idle",
          exists: false,
          registered: false,
          message: "Isi NIK 16 digit untuk memulai pengecekan data.",
        });
      }
      return;
    }
    if (lookupKeyRef.current === key) return;
    lookupKeyRef.current = key;
    if (lookupTimerRef.current) clearTimeout(lookupTimerRef.current);
    setAddLookup({
      status: "checking",
      exists: false,
      registered: false,
      message: "Sedang mengecek NIK.",
    });
    lookupTimerRef.current = setTimeout(async () => {
      const result = await axiosFetch({
        method: "GET",
        url: "/organisasi-pengurus-lookup/nik",
        auth: true,
        params: { nik },
      });
      if (result?.data?.success === false || !Object.prototype.hasOwnProperty.call(result?.data || {}, "exists")) {
        clearLookupFields(setValue);
        setValue("nik", nik);
        setAddLookup({
          status: "idle",
          exists: false,
          registered: false,
          message: result?.message || "Pengecekan NIK gagal dilakukan.",
        });
        return;
      }
      if (result?.data?.exists) {
        applyLookupResult(setValue, result.data);
        return;
      }
      clearLookupFields(setValue);
      setValue("nik", nik);
      setAddLookup({
        status: "new",
        exists: false,
        registered: false,
        message: "NIK belum tersedia. Silakan lengkapi data personal baru.",
      });
    }, 350);
  };

  const saveHandle = async (form, defaultValue = {}) => {
    const id = form?.id_pengurus_organisasi || defaultValue?.id_pengurus_organisasi || "";
    return axiosFetch({
      method: id ? "PUT" : "POST",
      url: id ? `/organisasi-pengurus/${id}` : "/organisasi-pengurus",
      auth: true,
      data: {
        no_kk: form?.no_kk || defaultValue?.no_kk || "",
        nik: form?.nik || defaultValue?.nik || "",
        gelar_depan: form?.gelar_depan || "",
        nama_lengkap: form?.nama_lengkap || "",
        gelar_belakang: form?.gelar_belakang || "",
        tempat_lahir: form?.tempat_lahir || "",
        tanggal_lahir: form?.tanggal_lahir || "",
        nomor_akta_lahir: form?.nomor_akta_lahir || "",
        tahun_akta_lahir: form?.tahun_akta_lahir || "",
        id_jenis_kelamin: Number(form?.id_jenis_kelamin || 0),
        golongan_darah: form?.golongan_darah || "",
        id_agama: Number(form?.id_agama || 0),
        kewarganegaraan: form?.kewarganegaraan || "",
        id_status_perkawinan: Number(form?.id_status_perkawinan || 0),
        hp: form?.hp || "",
        telepon: form?.telepon || "",
        email: form?.email || "",
        alamat: form?.alamat || "",
        kota: form?.kota || "",
        kode_pos: form?.kode_pos || "",
        photo_url: form?.photo_url || "",
        file_kartu_keluarga: form?.file_kartu_keluarga || "",
        file_ktp: form?.file_ktp || "",
        file_akta_lahir: form?.file_akta_lahir || "",
        file_rekening_bank: form?.file_rekening_bank || "",
        pekerjaan: form?.pekerjaan || "",
        jabatan: form?.jabatan_pekerjaan || "",
        bidang_usaha: form?.bidang_usaha || "",
        nama_tempat_kerja: form?.nama_tempat_kerja || "",
        telepon_tempat_kerja: form?.telepon_tempat_kerja || "",
        alamat_tempat_kerja: form?.alamat_tempat_kerja || "",
        pendapatan: form?.pendapatan || "",
        sumber_pendapatan: form?.sumber_pendapatan || "",
        npwp: form?.npwp || "",
        nama_ayah: form?.nama_ayah || "",
        pekerjaan_ayah: form?.pekerjaan_ayah || "",
        nama_ibu: form?.nama_ibu || "",
        pekerjaan_ibu: form?.pekerjaan_ibu || "",
        alamat_orang_tua: form?.alamat_orang_tua || "",
        nomor_rekening: form?.nomor_rekening || "",
        rekening_atas_nama: form?.rekening_atas_nama || "",
        bank: form?.bank || "",
        nomor_bpjs_kesehatan: form?.nomor_bpjs_kesehatan || "",
        status_bpjs_kesehatan: form?.status_bpjs_kesehatan || "",
        nomor_bpjs_ketenagakerjaan: form?.nomor_bpjs_ketenagakerjaan || "",
        status_bpjs_ketenagakerjaan: form?.status_bpjs_ketenagakerjaan || "",
        asuransi_lainnya: form?.asuransi_lainnya || "",
        nomor_asuransi_lainnya: form?.nomor_asuransi_lainnya || "",
        jabatan_pengurus: form?.jabatan_pengurus || "",
        tanggal_mulai: form?.tanggal_mulai || "",
      },
    });
  };

  const showProfilePDF = async (idDetail) => {
    const result = await axiosFetchProfilePDF({
      method: "GET",
      url: `/organisasi-pengurus/${idDetail}`,
      auth: true,
    });
    if (result?.data) {
      const photoDataURL = await loadPhotoDataURL(result.data?.photo_url, user?.token);
      setProfilePDFData({
        ...result.data,
        photo_data_url: photoDataURL,
      });
      toggleProfilePDFViewer();
    }
  };

  return (
    <>
      <DataList
        title="Pengurus"
        coltype={coltype}
        reload={reloadKey}
        dlist={{
        url: "/organisasi-pengurus",
        columns: [...listColumns, ...(canManage ? ["action"] : [])],
        filter: [
          { dtype: "q", name: "q", colsize: 3 },
          {
            dtype: "dropdown",
            name: "jabatan",
            label: "Jabatan",
            colsize: 3,
            options: jabatanOptions,
            valueId: "nama",
            labelId: "nama",
            placeholder: "-Semua Jabatan-",
          },
          {
            dtype: "dropdown",
            name: "aktif",
            label: "Status",
            colsize: 3,
            options: statusAktifOptions,
            valueId: "id",
            labelId: "nama",
            placeholder: "-Semua Status-",
          },
        ],
        sort_by: ["nik", "nama_lengkap", "jabatan", "hp", "email", "periode"],
      }}
      addconfig={canManage ? {
        columns: formColumns,
        title: "Tambah Pengurus",
        label_tool: "Tambah Pengurus",
        defaultValue: {},
        coltype: addColtype,
        size: "xl",
        saveDisabled: !isAddReadyToSave,
        saveHandle,
        onChangeForm: onAddFormChange,
        onCancel: resetAddLookup,
        onSuccess: resetAddLookup,
        children: (
          <div className="col-12">
            <div
              className={`alert mb-0 ${
                addLookup.status === "registered"
                  ? "alert-warning"
                  : addLookup.status === "new" || addLookup.status === "available"
                    ? "alert-info"
                    : "alert-secondary"
              }`}
            >
              {addLookup.message}
            </div>
          </div>
        ),
      } : null}
      editconfig={canManage ? {
        columns: ["id_pengurus_organisasi", "id_personal", ...formColumns],
        title: "Ubah Pengurus",
        url: "/organisasi-pengurus",
        urlitem: "/organisasi-pengurus",
        cellid: "id_pengurus_organisasi",
        coltype: editColtype,
        size: "xl",
        saveHandle,
        normalizeItem: (item) => ({ ...item, jabatan_pekerjaan: item?.jabatan_pekerjaan || "" }),
      } : null}
			delconfig={canDelete ? {
				url: "/organisasi-pengurus",
				cellid: "id_pengurus_organisasi",
        cellname: "nama_lengkap",
      } : null}
      activateconfig={canManage ? {
        url: "/organisasi-pengurus",
        cellid: "id_pengurus_organisasi",
        cellname: "nama_lengkap",
        cellactivate: "aktif",
        labelactivate: "Aktifkan",
        labelinactivate: "Non-aktifkan",
        cfmactivate: "mengaktifkan",
        cfminactivate: "menonaktifkan",
        infoactivate: "diaktifkan",
        infoinactivate: "dinonaktifkan",
      } : null}
        detailconfig={{
          url: "/organisasi-pengurus",
          cellid: "id_pengurus_organisasi",
          columns: detailColumns,
          title: "Detail Pengurus",
          size: "xl",
          tooltip: "Detail",
          use_card: false,
          renderContent: (item) => <PengurusProfileDetail item={item} />,
          footerContent: ({ idDetail }) => (
            <button
              type="button"
              className="btn btn-danger"
              disabled={loadingProfilePDF}
              onClick={() => showProfilePDF(idDetail)}
            >
              <i className="fas fa-file-pdf mr-1" />
              {loadingProfilePDF ? "Memuat PDF..." : "PDF"}
            </button>
          ),
        }}
        pdflist={{
          title: "Pengurus",
          columns: listColumns,
          orientation: "landscape",
        }}
        xlslist={{
          title: "Pengurus",
          columns: xlsColumns,
        }}
      />
      {profilePDFData
        ? getProfilePDFViewer({
            title: `Profil Pengurus - ${profilePDFData?.nama_lengkap || ""}`,
            content: <PengurusProfilePDF item={profilePDFData} />,
            viewerKey: `pengurus-profile-${profilePDFData?.id_pengurus_organisasi || ""}-${Date.now()}`,
          })
        : null}
    </>
  );
}
